/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import apiClient from 'data/api/apiClient';
import {
    CompanyDto,
    CompaniesListDto,
    CompanyDetailsDto,
    CompanyStoresListDto,
    CompanyStoresListQueryParams,
    CashboxesByTaxNumbersQueryParams,
    CashboxesByTaxNumbersDto,
    CompaniesListRequestParams,
    CompaniesListExcelRequestParams,
} from './companiesClient.typings';
import {
    cutEmptyParams,
    cutEmptyParamsIncludingArrays,
    DATA_UNAVAILABLE,
    makeArrayFromCommaSeparatedString,
} from '../../../utils/utils';
import { CashBoxesStats } from '../cashBoxes/cashBoxesClient.typings';
import {
    RegistryTurnoversByPeriodDto,
    RegistryTurnoversByObjectsDto,
    TurnoversByPeriodQueryParams,
    TurnoversByObjectsQueryParams,
    TurnoversByPeriodEXCELParams,
    TurnoversByTaxEntityEXCELParams,
} from '../apiCommon.typings';
import { CompaniesListTableData } from '../../../components/RegistryWidgets/Companies/CompaniesList/CompaniesListConfig';
import { RequestToCreateExcelDto } from '../downloadings/downloadingsClient.typings';

const SEARCH_TYPE_TO_SEARCH_PARAM: { [key in keyof CompaniesListTableData]?: string } = {
    name: 'names',
    taxNumber: 'taxNumbers',
};

const getSearchParamName = (searchType: keyof CompaniesListTableData): string => {
    let searchParam = SEARCH_TYPE_TO_SEARCH_PARAM[searchType];
    if (searchParam === undefined) searchParam = DATA_UNAVAILABLE;
    return searchParam;
};

const adjustCompaniesListFilterParams = (params: CompaniesListRequestParams | CompaniesListExcelRequestParams) => {
    const { search_type, search_text } = params;
    const queryParams = {
        ...params,
        [getSearchParamName(search_type as keyof CompaniesListTableData)]: makeArrayFromCommaSeparatedString(
            search_text,
            true,
        ),
    };
    delete queryParams.search_text;
    delete queryParams.search_type;

    return cutEmptyParamsIncludingArrays(queryParams);
};

async function listCompanies(params: CompaniesListRequestParams): Promise<CompaniesListDto> {
    return apiClient.post<CompaniesListDto>('1.0.0/companies', adjustCompaniesListFilterParams(params));
}

async function downloadCompaniesListAsExcel(params: CompaniesListExcelRequestParams): Promise<RequestToCreateExcelDto> {
    return apiClient.post<RequestToCreateExcelDto>('1.0.0/companies/excel', adjustCompaniesListFilterParams(params));
}

async function getCompany(taxNumber: string): Promise<CompanyDto & CompanyDetailsDto> {
    return apiClient.get<CompanyDto & CompanyDetailsDto>(`1.0.0/companies/${taxNumber}`);
}

async function getTaxpayerAccHomeData(): Promise<CompanyDto & CompanyDetailsDto> {
    return apiClient.get<CompanyDto & CompanyDetailsDto>('1.0.0/companies/home');
}

async function getCompanyStores(payload: {
    tax_number: string;
    params: CompanyStoresListQueryParams;
}): Promise<CompanyStoresListDto> {
    return apiClient.get<CompanyStoresListDto>(`1.0.0/companies/${payload.tax_number}/stores`, payload.params);
}

async function getCompanyCashBoxesStats(taxNumber: string): Promise<CashBoxesStats> {
    return apiClient.get<CashBoxesStats>(`1.0.0/companies/${taxNumber}/cashboxes/stats`);
}

async function getCompanyTurnoversByPeriod(
    taxNumber: string,
    params: TurnoversByPeriodQueryParams,
): Promise<RegistryTurnoversByPeriodDto[]> {
    const { ...newParams } = params;
    delete newParams.group;
    return apiClient.get<RegistryTurnoversByPeriodDto[]>(
        `1.0.0/companies/${taxNumber}/turnovers`,
        cutEmptyParams(newParams),
    );
}

async function getCompanyTurnoversByObjects(
    taxNumber: string,
    params: TurnoversByObjectsQueryParams,
): Promise<RegistryTurnoversByObjectsDto> {
    return apiClient.get<RegistryTurnoversByObjectsDto>(
        `1.0.0/companies/${taxNumber}/stores/turnovers`,
        cutEmptyParams(params),
    );
}

async function downloadCompanyTurnoversByPeriodAsExcel(
    taxNumber: string,
    params: TurnoversByPeriodEXCELParams,
): Promise<ArrayBuffer> {
    return apiClient.downloadFile(`1.0.0/companies/${taxNumber}/turnovers/excel`, cutEmptyParams(params));
}

async function downloadCompanyTurnoversByObjectsAsExcel(
    taxNumber: string,
    params: TurnoversByTaxEntityEXCELParams,
): Promise<ArrayBuffer> {
    return apiClient.downloadFile(`1.0.0/companies/${taxNumber}/stores/turnovers/excel`, cutEmptyParams(params));
}

async function fetchCashboxesByTaxNumbers(params: CashboxesByTaxNumbersQueryParams): Promise<CashboxesByTaxNumbersDto> {
    return apiClient.post<CashboxesByTaxNumbersDto>('1.0.0/companies/cashboxes', params);
}

export const companiesClient = {
    listCompanies,
    getCompany,
    getTaxpayerAccHomeData,
    getCompanyStores,
    downloadCompaniesListAsExcel,
    getCompanyCashBoxesStats,
    getCompanyTurnoversByPeriod,
    getCompanyTurnoversByObjects,
    downloadCompanyTurnoversByPeriodAsExcel,
    downloadCompanyTurnoversByObjectsAsExcel,
    fetchCashboxesByTaxNumbers,
};
